import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Image } from 'react-native';
import { Mail, ArrowRight, CheckCircle2, ChevronRight, Info, Lock } from '../lib/icons';
import { resetPassword, supabase } from '../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/constants';

// Import the carousel images - webpack will process these and provide the correct paths
// @ts-ignore - webpack will resolve these images
import carouselOneImage from '../assets/carousel_one.png';
// @ts-ignore - webpack will resolve this image
import carouselTwoImage from '../assets/carousel_2.png';
// @ts-ignore - webpack will resolve this image
import carouselThreeImage from '../assets/carousel_3.png';

const CAROUSEL_SLIDES = [
  {
    id: 1,
    title: "One dedicated partner for your home",
    description: "Get ongoing service with your dedicated home manager and HoneyDone handyman. For jobs that require a specialist, we'll help coordinate for you.",
    icon: <Info size={64} color="#000000" />,
    isFirstSlide: true
  },
  {
    id: 2,
    title: "Customized home maintenance",
    description: "We'll create a care plan based on your unique appliances and systems to keep your home safe and functional.",
    icon: <CheckCircle2 size={64} color="#000000" />,
    isFirstSlide: true
  },
  {
    id: 3,
    title: "Special offers to stay one step ahead",
    description: "Opt in to partner offers like window washing, gutter cleaning, carpet washing, and more",
    icon: <ArrowRight size={64} color="#000000" />,
    isFirstSlide: true
  }
];

const ClaimAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'carousel' | 'email' | 'password' | 'success'>('carousel');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState<any>(null);
  const [validatingToken, setValidatingToken] = useState(false);

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Handle token validation on mount
  useEffect(() => {
    const token = searchParams.get('token');
    
    if (token && !session && !validatingToken) {
      validateTokenAndRedirect(token);
    }
  }, [searchParams, session]);

  const validateTokenAndRedirect = async (token: string) => {
    try {
      setValidatingToken(true);
      setLoading(true);
      setError('');

      // Call the validate-claim-token edge function
      const functionUrl = `${SUPABASE_URL}/functions/v1/validate-claim-token`;
      
      const redirectTo = Platform.OS === 'web' && typeof window !== 'undefined'
        ? `${window.location.origin}/claim`
        : 'https://app.honeydone.com/claim';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ 
          token,
          redirectTo
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token validation error:', errorText);
        throw new Error('Invalid or expired token');
      }

      const functionData = await response.json();

      if (!functionData || functionData.error) {
        throw new Error(functionData?.error || 'Failed to validate token');
      }

      // Redirect to the recovery link - this will authenticate the user
      // and redirect back to /claim
      if (functionData.recoveryLink) {
        window.location.href = functionData.recoveryLink;
      } else {
        throw new Error('No recovery link received');
      }
    } catch (err: any) {
      console.error('Error validating token:', err);
      setError(err.message || 'Invalid or expired token. Please request a new claim link.');
      setValidatingToken(false);
      setLoading(false);
    }
  };

  const handleNextSlide = () => {
    if (currentSlide < CAROUSEL_SLIDES.length - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      if (session) {
        setStep('password');
      } else {
        setStep('email');
      }
    }
  };

  const handleClaim = async () => {
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await resetPassword(email);
      setStep('success');
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!password) {
      setError('Please enter a new password.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderCarousel = () => {
    const currentSlideData = CAROUSEL_SLIDES[currentSlide];
    const useHeroLayout = currentSlideData.isFirstSlide === true;

    return (
      <View style={styles.carouselContainer}>
        {useHeroLayout ? (
          <>
            {/* Logo Header */}
            <View style={styles.logoContainer}>
              <Image
                source={{ uri: 'https://storage.googleapis.com/hdwebsite/images/hd.png' }}
                style={styles.logo as any}
                resizeMode="contain"
              />
              <Text style={styles.logoText}>HoneyDone</Text>
            </View>

            {/* Headline */}
            <Text style={styles.headline}>{currentSlideData.title}</Text>

            {/* Subtitle (for slide 2) */}
            {(currentSlideData as any).subtitle ? (
              <Text style={styles.slideSubtitle}>{(currentSlideData as any).subtitle}</Text>
            ) : null}

            {/* Hero Image */}
            {currentSlide === 0 ? (
              Platform.OS === 'web' ? (
                <img
                  src={carouselOneImage}
                  alt="Hero image"
                  style={{
                    width: '100%',
                    height: 300,
                    borderRadius: 12,
                    objectFit: 'cover' as any,
                    marginBottom: 16,
                  }}
                />
              ) : (
                <Image
                  source={typeof carouselOneImage === 'string' ? { uri: carouselOneImage } : carouselOneImage}
                  style={styles.heroImage as any}
                  resizeMode="cover"
                />
              )
            ) : currentSlide === 1 ? (
              Platform.OS === 'web' ? (
                <img
                  src={carouselTwoImage}
                  alt="Hero image"
                  style={{
                    width: '100%',
                    height: 300,
                    borderRadius: 12,
                    objectFit: 'cover' as any,
                    marginBottom: 16,
                  }}
                />
              ) : (
                <Image
                  source={typeof carouselTwoImage === 'string' ? { uri: carouselTwoImage } : carouselTwoImage}
                  style={styles.heroImage as any}
                  resizeMode="cover"
                />
              )
            ) : currentSlide === 2 ? (
              Platform.OS === 'web' ? (
                <img
                  src={carouselThreeImage}
                  alt="Hero image"
                  style={{
                    width: '100%',
                    height: 300,
                    borderRadius: 12,
                    objectFit: 'cover' as any,
                    marginBottom: 16,
                  }}
                />
              ) : (
                <Image
                  source={typeof carouselThreeImage === 'string' ? { uri: carouselThreeImage } : carouselThreeImage}
                  style={styles.heroImage as any}
                  resizeMode="cover"
                />
              )
            ) : (
              <View style={styles.heroImageContainer}>
                <Text style={styles.heroImagePlaceholder}>Hero Image</Text>
              </View>
            )}

            {/* Description */}
            <Text style={styles.slideDescription}>{currentSlideData.description}</Text>
          </>
        ) : (
          <View style={styles.slideContent}>
            <View style={styles.iconContainer}>
              {currentSlideData.icon}
            </View>
            <Text style={styles.slideTitle}>{currentSlideData.title}</Text>
            <Text style={styles.slideDescription}>{currentSlideData.description}</Text>
          </View>
        )}

        <View style={styles.paginationContainer}>
          {CAROUSEL_SLIDES.map((_, index) => (
            <View
              key={index}
              style={[
                styles.paginationDot,
                index === currentSlide && styles.paginationDotActive
              ]}
            />
          ))}
        </View>

        <Pressable onPress={handleNextSlide} style={styles.nextButton}>
          <Text style={styles.nextButtonText}>
            {currentSlide === CAROUSEL_SLIDES.length - 1 ? "Get Started" : "Next"}
          </Text>
          <ChevronRight size={20} color="#ffffff" />
        </Pressable>
      </View>
    );
  };

  const renderEmailForm = () => (
    <View style={styles.formContainer}>
      <View style={styles.header}>
        <Text style={styles.title}>Claim Your Account</Text>
        <Text style={styles.subtitle}>
          Enter your email address to verify your account and set your password.
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.label}>Email Address</Text>
        <View style={styles.inputWrapper}>
          <View style={styles.inputIcon}>
            <Mail size={20} color="#9ca3af" />
          </View>
          <TextInput
            style={styles.input}
            value={email}
            onChangeText={setEmail}
            placeholder="Enter your email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
          />
        </View>
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <Pressable
        onPress={handleClaim}
        disabled={loading}
        style={({ pressed }) => [
          styles.submitButton,
          (loading || pressed) && styles.submitButtonDisabled
        ]}
      >
        {loading ? (
          <ActivityIndicator size="small" color="#ffffff" />
        ) : (
          <Text style={styles.submitButtonText}>Continue</Text>
        )}
      </Pressable>
    </View>
  );

  const renderPasswordForm = () => (
    <View style={styles.formContainer}>
      <View style={styles.header}>
        <Text style={styles.title}>Set Your Password</Text>
        <Text style={styles.subtitle}>
          Create a secure password to access your account.
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.label}>New Password</Text>
        <View style={styles.inputWrapper}>
          <View style={styles.inputIcon}>
            <Lock size={20} color="#9ca3af" />
          </View>
          <TextInput
            style={styles.input}
            value={password}
            onChangeText={setPassword}
            placeholder="Enter new password"
            secureTextEntry
            autoCapitalize="none"
          />
        </View>
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <Pressable
        onPress={handleUpdatePassword}
        disabled={loading}
        style={({ pressed }) => [
          styles.submitButton,
          (loading || pressed) && styles.submitButtonDisabled
        ]}
      >
        {loading ? (
          <ActivityIndicator size="small" color="#ffffff" />
        ) : (
          <Text style={styles.submitButtonText}>Set Password & Continue</Text>
        )}
      </Pressable>
    </View>
  );

  const renderSuccess = () => (
    <View style={styles.successContainer}>
      <CheckCircle2 size={64} color="#16a34a" />
      <Text style={styles.successTitle}>Check your email</Text>
      <Text style={styles.successDescription}>
        We've sent instructions to <Text style={{ fontWeight: 'bold' }}>{email}</Text> to set your password.
      </Text>
      <Pressable
        onPress={() => navigate('/login')}
        style={styles.loginButton}
      >
        <Text style={styles.loginButtonText}>Go to Login</Text>
      </Pressable>
    </View>
  );

  // Show loading state while validating token
  if (validatingToken) {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <ActivityIndicator size="large" color="#000000" />
          <Text style={styles.loadingText}>Validating claim link...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {step === 'carousel' && renderCarousel()}
        {step === 'email' && renderEmailForm()}
        {step === 'password' && renderPasswordForm()}
        {step === 'success' && renderSuccess()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: Platform.OS === 'web' ? ('100vh' as any) : '100%',
  },
  content: {
    width: '100%',
    maxWidth: 480,
    padding: 24,
    alignItems: 'center',
  },
  carouselContainer: {
    width: '100%',
    alignItems: 'center',
    gap: 32,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  logo: {
    width: 32,
    height: 32,
  },
  logoText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1b1b1b',
    marginLeft: 8,
  },
  headline: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 8,
  },
  slideSubtitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#14b8a6',
    textAlign: 'center',
    marginBottom: 16,
  },
  heroImage: {
    width: '100%',
    height: 300,
    borderRadius: 12,
    marginBottom: 16,
  },
  heroImageContainer: {
    width: '100%',
    height: 300,
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  heroImagePlaceholder: {
    fontSize: 16,
    color: '#9ca3af',
  },
  slideContent: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },
  iconContainer: {
    marginBottom: 16,
    width: 120,
    height: 120,
    backgroundColor: '#f3f4f6',
    borderRadius: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  slideTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
  },
  slideDescription: {
    fontSize: 16,
    color: '#4b5563',
    textAlign: 'center',
    lineHeight: 24,
  },
  paginationContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  paginationDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#d1d5db',
  },
  paginationDotActive: {
    backgroundColor: '#000000',
    width: 24,
  },
  nextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000000',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    width: '100%',
    gap: 8,
  },
  nextButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  formContainer: {
    width: '100%',
    gap: 24,
  },
  header: {
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#4b5563',
    textAlign: 'center',
  },
  inputContainer: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  inputIcon: {
    paddingLeft: 12,
  },
  input: {
    flex: 1,
    padding: 12,
    fontSize: 16,
    color: '#111827',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
    textAlign: 'center',
  },
  submitButton: {
    backgroundColor: '#000000',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  successContainer: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  successDescription: {
    fontSize: 16,
    color: '#4b5563',
    textAlign: 'center',
    marginBottom: 16,
  },
  loginButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  loginButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
});

export default ClaimAccount;
